import type { KeyboardEvent } from 'react';

const ARROW_KEYS = ['ArrowDown', 'ArrowRight', 'ArrowUp', 'ArrowLeft'];

/**
 * The keyboard half of `ReasonChipGroup`: a number key chooses that reason from anywhere in the
 * form, and an arrow on a chip moves to the neighbour. Returns whether the key was taken.
 *
 * Called from the form's own `onKeyDown` rather than the group's, because `1` has to work with
 * focus on the Cancel button as much as on a chip. The form has already ruled out typing.
 *
 * Selection follows focus, which is what a radiogroup means by an arrow key. Moving only the focus
 * let the ring sit on one reason while the chosen one was another, so confirming sent the reason
 * behind the ring, or, from a fresh form, sent nothing at all.
 */
export function chooseReasonByKey<Reason extends string>(
	event: KeyboardEvent<HTMLElement>,
	order: readonly Reason[],
	onChoose: (reason: Reason) => void,
): boolean {
	const reasonIndex = Number(event.key) - 1;
	if (/^[1-9]$/.test(event.key) && reasonIndex < order.length) {
		event.preventDefault();
		onChoose(order[reasonIndex]);
		event.currentTarget.querySelectorAll<HTMLElement>('[role="radio"]')[reasonIndex]?.focus();
		return true;
	}

	const current = (event.target as Element).closest<HTMLElement>('[role="radio"]');
	if (!current || !ARROW_KEYS.includes(event.key)) return false;
	event.preventDefault();
	const radios = Array.from(event.currentTarget.querySelectorAll<HTMLElement>('[role="radio"]'));
	const offset = event.key === 'ArrowDown' || event.key === 'ArrowRight' ? 1 : -1;
	const nextIndex = (radios.indexOf(current) + offset + radios.length) % radios.length;
	onChoose(order[nextIndex]);
	radios[nextIndex].focus();
	return true;
}
